import React, { useCallback, useState, useEffect } from 'react';
import Button from '../../../shared/components/ui/Button';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { getAllInvoices, getAllUsers } from '../../services/api/implementation/impl';
import { getAllCompanies } from '../../../builder/services/api/implementation/impl';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../../contexts/ProjectsContext';

const schema = yup.object().shape({
  builder_id: yup.number().required('Builder name is required'),
  company_id: yup.number().required('Company name is required'),
  project_id: yup.number().required('Project name is required'),
  invoice_id: yup.number().required('Invoice number is required'),
  amount: yup
    .number()
    .typeError('Amount paid must be a number')
    .positive('Amount paid must be positive')
    .required('Amount paid is required')
});

export const BillingHistoryForm = ({ onClose, onSubmit }) => {
  // State for selected values
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [selectedBuilder, setSelectedBuilder] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedProject, setSelectedProject] = useState('');

  const userId = Cookies.get('userId');

  // Track form view
  useEffect(() => {
    mixpanel.track('Super Admin Billing History Form Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Fetch data using React Query
  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => getAllUsers(),
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Billing History Form Users Fetch Failed', {
          user_id: userId,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      mixpanel.track('Super Admin Billing History Form Users Fetch Success', {
        user_id: userId,
        users_count: result?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Billing History Form Users Fetch Failed', {
        user_id: userId,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getAllCompanies(),
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Billing History Form Companies Fetch Failed', {
          user_id: userId,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      mixpanel.track('Super Admin Billing History Form Companies Fetch Success', {
        user_id: userId,
        companies_count: result?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Billing History Form Companies Fetch Failed', {
        user_id: userId,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const { data: invoices } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => getAllInvoices(),
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Billing History Form Invoices Fetch Failed', {
          user_id: userId,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      mixpanel.track('Super Admin Billing History Form Invoices Fetch Success', {
        user_id: userId,
        invoices_count: result?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Billing History Form Invoices Fetch Failed', {
        user_id: userId,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  // Transform data for dropdowns
  const transformedBuilders = users?.data?.map((item) => ({
    value: item.id,
    label: item.primery_user?.name?.trim()
  }));

  const transformedCompanies = companies
    ?.filter((company) => company.builder.id === parseInt(selectedBuilder))
    ?.map((item) => ({
      value: item.id,
      label: item.company_name?.trim()
    }));

  const transformedProjects = projects
    ?.filter((project) => project.company_id === parseInt(selectedCompany))
    ?.map((item) => ({
      value: item.id,
      label: item.name?.trim()
    }));

  const transformedInvoices = invoices
    ?.filter((invoice) => invoice.project_id === parseInt(selectedProject))
    ?.map((item) => ({
      value: item.id,
      label: item.invoice_number?.trim()
    }));

  // Handle dropdown changes
  const handleBuilderChange = (e) => {
    const builderId = e.target.value;

    mixpanel.track('Super Admin Billing History Form Builder Selected', {
      user_id: userId,
      builder_id: builderId,
      timestamp: new Date().toISOString()
    });

    setSelectedBuilder(builderId);
    setSelectedCompany(''); // Reset company and project when builder changes
    setSelectedProject('');
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;

    mixpanel.track('Super Admin Billing History Form Company Selected', {
      user_id: userId,
      company_id: companyId,
      builder_id: selectedBuilder,
      timestamp: new Date().toISOString()
    });

    setSelectedCompany(companyId);
    setSelectedProject(''); // Reset project when company changes
  };

  const handleProjectChange = (e) => {
    const projectId = e.target.value;

    mixpanel.track('Super Admin Billing History Form Project Selected', {
      user_id: userId,
      project_id: projectId,
      company_id: selectedCompany,
      builder_id: selectedBuilder,
      timestamp: new Date().toISOString()
    });

    setSelectedProject(projectId);
  };

  const handleInvoiceChange = (e) => {
    const invoiceId = e.target.value;

    mixpanel.track('Super Admin Billing History Form Invoice Selected', {
      user_id: userId,
      invoice_id: invoiceId,
      project_id: selectedProject,
      company_id: selectedCompany,
      builder_id: selectedBuilder,
      timestamp: new Date().toISOString()
    });
  };

  // Hook form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm({
    resolver: yupResolver(schema)
  });

  const handleAmountPaid = useCallback(
    async (val) => {
      const newval = val.replace(/[^0-9]/g, '');
      setValue('amount', newval);

      // Track amount input
      if (newval) {
        mixpanel.track('Super Admin Billing History Form Amount Changed', {
          user_id: userId,
          amount: newval,
          timestamp: new Date().toISOString()
        });
      }
    },
    [setValue, userId]
  );

  const handleFormSubmit = (data) => {
    mixpanel.track('Super Admin Billing History Form Submitted', {
      user_id: userId,
      builder_id: data.builder_id,
      company_id: data.company_id,
      project_id: data.project_id,
      invoice_id: data.invoice_id,
      amount: data.amount,
      timestamp: new Date().toISOString()
    });

    onSubmit(data);
  };

  const handleFormCancel = () => {
    mixpanel.track('Super Admin Billing History Form Cancelled', {
      user_id: userId,
      builder_selected: !!selectedBuilder,
      company_selected: !!selectedCompany,
      project_selected: !!selectedProject,
      timestamp: new Date().toISOString()
    });

    onClose();
  };

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center w-full">
          <div className="flex flex-col gap-2 w-[48%]">
            <label className="font-medium">Builder Name *</label>
            <select
              className="w-full p-2 border-[1px] border-stone-300 rounded"
              {...register('builder_id')}
              value={selectedBuilder}
              onChange={handleBuilderChange}>
              <option value="">Select builder</option>
              {transformedBuilders?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {errors.builder_id && <span className="text-red-600">{errors.builder_id.message}</span>}
          </div>

          <div className="flex flex-col gap-2 w-[48%]">
            <label className="font-medium">Company Name *</label>
            <select
              className="w-full p-2 border-[1px] border-stone-300 rounded"
              {...register('company_id')}
              value={selectedCompany}
              onChange={handleCompanyChange}
              disabled={!selectedBuilder}>
              <option value="">Select company</option>
              {transformedCompanies?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {errors.company_id && <span className="text-red-600">{errors.company_id.message}</span>}
          </div>
        </div>

        <div className="flex justify-between items-center w-full">
          <div className="flex flex-col gap-2 w-[48%]">
            <label className="font-medium">Project Name *</label>
            <select
              className="w-full p-2 border-[1px] border-stone-300 rounded"
              {...register('project_id')}
              value={selectedProject}
              onChange={handleProjectChange}
              disabled={!selectedCompany}>
              <option value="">Select project</option>
              {transformedProjects?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {errors.project_id && <span className="text-red-600">{errors.project_id.message}</span>}
          </div>

          <div className="flex flex-col gap-2 w-[48%]">
            <label className="font-medium">Invoice *</label>
            <select
              className="w-full p-2 border-[1px] border-stone-300 rounded"
              {...register('invoice_id')}
              disabled={!selectedProject}
              onChange={handleInvoiceChange}>
              <option value="">Select invoice</option>
              {transformedInvoices?.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {errors.invoice_id && <span className="text-red-600">{errors.invoice_id.message}</span>}
          </div>
        </div>

        <div className="flex flex-col justify-start items-start gap-2 w-full">
          <label className="font-medium">Amount Paid *</label>
          <input
            className="w-full p-2 border-[1px] border-stone-300 rounded"
            type="text"
            {...register('amount')}
            onChange={(e) => handleAmountPaid(e.target.value)}
            maxLength={10}
            placeholder="Enter amount paid"
          />
          {errors.amount && <span className="text-red-600">{errors.amount.message}</span>}
        </div>
      </div>

      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button type="reset" onClick={handleFormCancel} displayType="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit(handleFormSubmit)} type="submit">
          Submit
        </Button>
      </div>
    </>
  );
};
